import React, { useEffect, useState } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "./useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/HousekeepingFields.json";
import { GetAll, Delete, UpdateStatus } from "adminApi/HousekeepingApi/Housekeeping.js";
import { showSuccess, showError } from "utils/notifications";
import ConfirmModal from "./ConfirmModal";
import FormSelect from "Common/Custom/FormSelect";

// Status options for the per-room status dropdown inside the modal
const ROOM_STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
];

const HousekeepingListTable = () => {
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const debouncedSearch = useDebouncedValue(search, 500);
    const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
    const sizeOptions = [10, 20, 50, 100];
    const { tabledata } = fields;

    // ── Paginated list ─────────────────────────────────────────────────────
    const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAll, {
        page,
        limit,
        search: debouncedSearch,
        title: "Housekeeping List",
    });

    // ── Room Status modal state ────────────────────────────────────────────
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [roomDetailsArray, setRoomDetailsArray] = useState([]);
    const [saving, setSaving] = useState(false);

    const openModal = (rowData) => {
        setSelectedRow(rowData);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedRow(null);
    };

    // Parse room_details string ("102:pending, 103:completed") into array
    useEffect(() => {
        if (!selectedRow) return;

        const roomDetailsString = data.find(
            (item) => item.housekeeping_id === selectedRow.housekeeping_id
        )?.room_details;

        const detailsArray = roomDetailsString
            ? roomDetailsString.split(",").map((item) => {
                const [no, status] = item.trim().split(":");
                return { no: no?.trim(), status: status?.trim() || "pending" };
            })
            : [];

        setRoomDetailsArray(detailsArray);
    }, [selectedRow, data]);

    // Update a single room's status in the local array
    const handleRoomStatusChange = (index, newStatus) => {
        setRoomDetailsArray((prev) =>
            prev.map((room, i) => (i === index ? { ...room, status: newStatus } : room))
        );
    };

    // ── Table config ───────────────────────────────────────────────────────
    const displayFields = generateDisplayFields(tabledata);

    // Transform inventory_issued 0/1 → badge — done at render time, not in data
    const formattedRawData = data.map((item) => ({
        ...item,
        inventory_issued:
            item.inventory_issued == "1" ? "Yes" : "No",
    }));

    const { formattedData, columns } = useTableConfig({
        data: formattedRawData,
        page,
        limit,
        canShowAction,
        refetch,
        config: {
            entityName: "Housekeeping",
            editUrl: "edit-housekeeping",
            idKey: "housekeeping_id",
            deleteApi: Delete,
            displayFields,
        },
        IsModel: {
            show: true,
            val: "Room Status",
            onClick: openModal,
        },
    });

    // ── Handlers ───────────────────────────────────────────────────────────
    const handleSearch = (val, resetPage = null) => {
        setSearch(val);
        if (resetPage !== null) setPage(resetPage);
    };

    const onPageChange = (p) => setPage(p);
    const onLimitChange = (newLimit) => { setLimit(newLimit); setPage(1); };

    const handleSaveStatus = async () => {
        if (!selectedRow?.housekeeping_id) return;
        setSaving(true);
        try {
            const response = await UpdateStatus({
                id: selectedRow.housekeeping_id,
                data: { room_details: roomDetailsArray },
            });

            if (response?.data?.success) {
                showSuccess("Housekeeping status updated successfully.");
                refetch();
                closeModal();
            } else {
                showError(response?.data?.error || "Failed to update housekeeping status.");
            }
        } catch {
            showError("An unexpected error occurred while updating.");
        } finally {
            setSaving(false);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <>
            <DataTable
                columns={columns}
                data={formattedData}
                options={{
                    loading,
                    error,
                    sortableColumns: displayFields.map((f) => f.accessor),
                    copy: true,
                    csv: true,
                    pdf: true,
                    print: true,
                    fileName: "Housekeeping List",
                }}
                Search={{
                    search: true,
                    SearchInputValue: search,
                    handleSearch,
                }}
                Entries={{
                    Entries: true,
                    limit,
                    onLimitChange,
                    options: sizeOptions,
                }}
                pagination={{
                    isActive: true,
                    meta,
                    onPageChange,
                }}
            />

            {/* Room Status Modal */}
            <ConfirmModal
                show={showModal}
                onHide={closeModal}
                title="Update Room Status"
                onConfirm={handleSaveStatus}
                loading={saving}
                confirmText="Save Changes"
                body={
                    selectedRow && (
                        <table className="table table-bordered mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Room No</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roomDetailsArray.map((room, index) => (
                                    <tr key={`${room.no}-${index}`}>
                                        <td className="align-middle fw-semibold">
                                            {room.no}
                                        </td>
                                        <td>
                                            <FormSelect
                                                field={{
                                                    name: `room_status_${index}`,
                                                    type: "select",
                                                    required: false,
                                                    fieldSize: "w-100",
                                                    labelHide: true,
                                                }}
                                                value={room.status}
                                                handleChange={(e) =>
                                                    handleRoomStatusChange(index, e.target.value)
                                                }
                                                options={ROOM_STATUS_OPTIONS}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                }
            />
        </>
    );
};

export default HousekeepingListTable;
